// E08-t2 — ONE FORMAT'S PUBLISHED DASHBOARD, INSIDE THE APP.
//
// Dashboards.md f2: "the pair's rendered dashboard is SERVED from R2". E05-t3
// proved the served bytes ARE the published artifact — a complete HTML document
// — and E06-t3 proved WHICH document depends on the reader's role. So this page
// does not re-render anything, does not fetch the payload and does not
// reimplement one control: it hands the reader the artifact the forge published,
// in a frame, on the app's own origin.
//
// WHY A FRAME AND NOT A REDIRECT: the stable url IS a first-class entry point
// (the Dashboards list links straight to it, and a reader may open it cold), but
// inside the app a dashboard has to sit under the shell — the org switcher, the
// KB switcher and the nav are how a person gets anywhere else. A frame is what
// keeps both true of one set of bytes.
//
// WHY THE FRAME IS SAFE HERE, stated because framing usually is not:
//   · SAME ORIGIN. vite.config.ts proxies /:org/:kb/dashboard/** to the Worker,
//     so the frame's document is on the app's origin and the `dj_session` cookie
//     is sent with it AND with every request the engine's own client makes from
//     inside it — the command POST, the ledger, the on-demand document bodies.
//   · NOTHING IS INJECTED. The app never reaches into the frame. What the reader
//     can do there is decided by the BYTES the Worker chose for their role
//     (writeUrl wired for a maintainer, null for a viewer), which is the whole
//     point of publishing the downgrade instead of hiding controls in a client.
//   · NO FREE-FORM EDITOR (Forge f2). Every write the framed page can make is a
//     declared form posting to POST /api/:org/:kb/command or the per-KB ledger.
//
// The dashboard's OWN title is deliberately not repeated in the app chrome: the
// artifact prints it, and printing it twice would leave a reader unable to tell
// which of the two surfaces they are looking at.
//
// 2026-08-07 design audit — THE HEADER ROW IS GONE. AppShell's contract for
// this altitude is that the artifact owns every pixel under the bar; the format
// name now lives in the bar's switcher, and the stable-url offer ("the stable
// url is a shareable address, so it is offered as one") moved with it, into the
// switcher popover's footer (src/components/DashboardSwitcher.tsx).
import { useState } from 'react';

import { Skeleton } from '@astryxdesign/core/Skeleton';

import type { ResolvedTheme } from '../theme-mode';

import './dashboard-view.css';

export interface DashboardViewProps {
  /** orgs.name — the :org path segment */
  org: string;
  /** kbs.name — the :kb path segment */
  kb: string;
  /** the kb_record MAP KEY — the name the render published under */
  format: string;
  /** THE APP'S THEME, resolved to the artifact's light|dark (Felipe's ask,
   *  2026-08-07: the dashboard follows the app). The root has already written
   *  it to the shared storage key the artifact BOOTS from (theme-mode.ts), so
   *  syncing an open frame is one honest move: re-key it and let it boot
   *  again — never reach inside (the no-injection rule above stands). */
  artifactTheme?: ResolvedTheme;
}

/** git-host/src/serve.ts SERVE_ROUTE — the stable per-format url, spelled once. */
export function stableDashboardUrl(org: string, kb: string, format: string): string {
  return `/${encodeURIComponent(org)}/${encodeURIComponent(kb)}/dashboard/${encodeURIComponent(format)}`;
}

export function DashboardView({ org, kb, format, artifactTheme }: DashboardViewProps) {
  const url = stableDashboardUrl(org, kb, format);
  return (
    <div className="dj-dashboard-view" data-page="dashboard" data-format={format}>
      {/* keyed on the url so the loading state resets when the reader switches
          formats — the frame element is reused by React otherwise, and a
          stale `loaded` would skip the skeleton for the NEW document — and on
          the theme, so a theme change re-boots the frame into the palette the
          root just wrote (absent, the key degrades to the url alone) */}
      <FramedDashboard key={artifactTheme ? `${url} ${artifactTheme}` : url} url={url} format={format} />
    </div>
  );
}

function FramedDashboard({ url, format }: { url: string; format: string }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <div style={{ position: 'relative', minHeight: 0, flex: 1 }}>
      <iframe
        className="dj-dashboard-view-frame"
        title={`${format} dashboard`}
        src={url}
        onLoad={() => setLoaded(true)}
      />
      {!loaded ? (
        // The design's loading face: a skeleton dashboard — one header band
        // over a grid of tiles — over the frame until the document answers.
        // aria-hidden: the frame's own title is the accessible story.
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gridTemplateRows: 'auto 1fr 1fr',
            gap: 'var(--spacing-3)',
            padding: 'var(--spacing-4)',
            background: 'var(--color-background-card)',
          }}
        >
          <Skeleton height="40px" style={{ gridColumn: '1 / -1' }} />
          <Skeleton />
          <Skeleton />
          <Skeleton />
          <Skeleton />
        </div>
      ) : null}
    </div>
  );
}
